import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { TableShell } from '../../components/molecules/TableShell'
import { StoreFormModal } from './StoreFormModal'
import { useStores, type StoreDto } from '../../../viewmodels/useStores'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /manage/stores — operator-managed catalog of tenant stores.
 *
 * The default store row (is_default = 1, slug='uyyamadilu') is the
 * build-time STORE.ID seed: Edit + Delete are disabled on it. Every
 * row gets a Publish button that's disabled today — the subdomain-
 * spin-up flow will wire it up later. Non-default rows can be added,
 * edited, and soft-deleted.
 */
export function StoresPage(): JSX.Element {
  const vm = useStores()
  const { can } = useAuth()
  const canAdd = can(SCREENCODES.MANAGESTORES, 'add')
  const canEdit = can(SCREENCODES.MANAGESTORES, 'edit')
  const canDelete = can(SCREENCODES.MANAGESTORES, 'delete')

  const [modalMode, setModalMode] = useState<'closed' | 'create' | 'edit'>('closed')
  const [editTarget, setEditTarget] = useState<StoreDto | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const openCreate = (): void => {
    setEditTarget(null)
    setModalMode('create')
  }
  const openEdit = (row: StoreDto): void => {
    setEditTarget(row)
    setModalMode('edit')
  }
  const closeModal = (): void => setModalMode('closed')

  const handleDelete = async (row: StoreDto): Promise<void> => {
    if (typeof window !== 'undefined' && !window.confirm(`Delete store "${row.display_name}"?`)) return
    setDeletingId(row.id)
    setDeleteError(null)
    const res = await vm.remove(row.id)
    setDeletingId(null)
    if (!res.ok) setDeleteError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-h2 text-charcoal">Stores</h1>
          <p className="font-body text-body-sm text-text-secondary mt-1">
            Tenant stores. The <span className="font-medium">default</span> store cannot be
            edited or deleted — it's the seed for the build-time per-store identity.
            Publish is wired in but not yet active (the subdomain-spin-up flow lands later).
          </p>
        </div>
        {canAdd ? <Button onClick={openCreate}>Add</Button> : null}
      </header>

      {deleteError !== null ? <Alert tone="error">{deleteError}</Alert> : null}
      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => vm.reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {vm.isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : vm.items.length === 0 ? (
        <EmptyState />
      ) : (
        <Table
          items={vm.items}
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={openEdit}
          onDelete={(row) => void handleDelete(row)}
          deletingId={deletingId}
        />
      )}

      <StoreFormModal
        open={modalMode !== 'closed'}
        mode={modalMode === 'edit' ? 'edit' : 'create'}
        initial={editTarget}
        onClose={closeModal}
        onSubmit={async (input) => {
          if (modalMode === 'edit' && editTarget !== null) {
            return vm.update(editTarget.id, input)
          }
          return vm.add(input as Parameters<typeof vm.add>[0])
        }}
      />
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No stores yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Use <span className="font-medium">Add</span> at the top right to create a new
        tenant store.
      </p>
    </div>
  )
}

function StatusPill({ status }: { readonly status: StoreDto['status'] }): JSX.Element {
  // Four tones for the four lifecycle stages.
  const styles: Record<StoreDto['status'], string> = {
    draft:      'border border-border bg-parchment/60 text-text-secondary',
    verified:   'border border-forest/30 bg-forest/10 text-forest',
    published:  'border border-clay/40 bg-clay/10 text-clay',
    suspended:  'border border-border bg-parchment/60 text-text-secondary line-through'
  }
  const labels: Record<StoreDto['status'], string> = {
    draft:     'Draft',
    verified:  'Verified',
    published: 'Published',
    suspended: 'Suspended'
  }
  return (
    <span
      className={[
        'inline-flex items-center rounded-full px-2.5 py-0.5 font-body text-body-xs',
        styles[status]
      ].join(' ')}
    >
      {labels[status]}
    </span>
  )
}

function Table({
  items,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  deletingId
}: {
  readonly items: StoreDto[]
  readonly canEdit: boolean
  readonly canDelete: boolean
  readonly onEdit: (row: StoreDto) => void
  readonly onDelete: (row: StoreDto) => void
  readonly deletingId: string | null
}): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Slug</Th>
            <Th>Display name</Th>
            <Th>Status</Th>
            <Th>Default</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const isDefault = row.is_default === 1
            return (
              <tr key={row.id} className="border-t border-border">
                <Td className="font-mono">{row.slug}</Td>
                <Td>{row.display_name}</Td>
                <Td><StatusPill status={row.status} /></Td>
                <Td>
                  {isDefault ? (
                    <span className="inline-flex items-center rounded-full border border-clay/40 bg-clay/10 px-2.5 py-0.5 font-body text-body-xs text-clay">
                      Default
                    </span>
                  ) : (
                    <span className="text-text-secondary">—</span>
                  )}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled
                    title="Publish flow coming soon."
                  >
                    Publish
                  </Button>{' '}
                  {canEdit ? (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onEdit(row)}
                        disabled={isDefault}
                        title={isDefault ? 'Default store cannot be modified.' : undefined}
                      >
                        Edit
                      </Button>{' '}
                    </>
                  ) : null}
                  {canDelete ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(row)}
                      disabled={isDefault || deletingId === row.id}
                      title={isDefault ? 'Default store cannot be deleted.' : undefined}
                    >
                      {deletingId === row.id ? 'Deleting…' : 'Delete'}
                    </Button>
                  ) : null}
                </Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}
